import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductsBy, fetchCountBy } from "store/slices/productsSlice";

import {
  ErrorMessage,
  Pagination,
  ProductsSkeleton,
  ProductToolbar,
} from "common";
import useSupersedingRequest from "hooks/useSupersedingRequest";
import useUrlState from "hooks/useUrlState";

import { ListProduct } from "..";
import { selectFavoritesLoading } from "../../redux/slices/favoritesSlice";

export default function ListProductNoFilter({ searchIds, by }) {
  // The whole listing state lives in the URL, mirroring `ListProductComponent` — this
  // listing has no filters, only sort/perPage/page/view.
  const [
    { page: rawPage, view: rawView, sort: rawSort, perPage: rawPerPage },
    setUrlState,
  ] = useUrlState({
    page: 1,
    view: "grid",
    sort: "",
    perPage: 12,
  });
  // Only a positive integer is a page. Anything else — `-1`, `-1.2`, `2.2`, `0` — is not
  // a near-miss to be salvaged but a malformed URL, so it falls back to 1 rather than
  // being rounded into a page the user never asked for. The hook has already turned
  // non-numeric and non-finite values into 1.
  //
  // This runs on read, before any fetch, so a malformed page never reaches the offset
  // maths. Only the upper bound has to wait for the count; see further down.
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const view = rawView === "list" ? "list" : "grid";
  const sort = rawSort === "asc" || rawSort === "desc" ? rawSort : "";
  const perPage = [12, 24, 48].includes(rawPerPage) ? rawPerPage : 12;
  const setPage = (next) => setUrlState({ page: next });
  const setView = (next) => setUrlState({ view: next });

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  // Convert searchIds string to array for favoriteItems - memoized to prevent dependency issues
  const processedSearchIds = useMemo(() => {
    if (by === "favoriteItems") {
      if (!searchIds) return [];
      return searchIds
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "")
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));
    }
    return searchIds;
  }, [by, searchIds]);

  // Handle array IDs for favoriteItems to match Redux slice logic
  let effectiveId;
  if (by === "favoriteItems" && Array.isArray(processedSearchIds)) {
    effectiveId =
      processedSearchIds.length > 0
        ? [...processedSearchIds].sort((a, b) => a - b).join(",")
        : "empty-favorites";
  } else {
    effectiveId = processedSearchIds;
  }

  // Key structure with pagination and sorting
  const minPrice = "";
  const maxPrice = "";
  const searchByName = "";
  const priceKey = `${minPrice}_${maxPrice}`;
  const searchKey = searchByName ? encodeURIComponent(searchByName) : "";
  const categoriesForCache = "";
  const brandsForCache = "";
  const manufacturersForCache = "";
  const key = `${effectiveId}_${perPage}_${sort}_${priceKey}_${searchKey}_${categoriesForCache}_${brandsForCache}_${manufacturersForCache}_${page}`;

  // Server-side pagination - get products for current page
  const products = useSelector(
    (state) => state.products.productsByPage[key] || []
  );
  const isLoading = useSelector((state) => state.products.isLoading);
  const error = useSelector((state) => state.products.error);
  const favoritesLoading = useSelector(selectFavoritesLoading);

  // Get total count from Redux store
  const countKey = `${effectiveId}_${priceKey}_${searchKey}_${categoriesForCache}_${brandsForCache}_${manufacturersForCache}`;
  const total = useSelector((state) => {
    const count = state.products.totalCounts?.[countKey];
    return count !== undefined ? count : 0;
  });
  const totalPages = Math.ceil(total / perPage) || 1;

  // A URL can name a page that doesn't exist (`?page=234234`), so once the real count
  // is in, land the user on the last page instead. `total > 0` matters: it's 0 until the
  // count arrives, and treating that as "one page" would bounce a perfectly good deep
  // link to page 50 on the very first render.
  //
  // Nothing here guards the *count* fetch — that was the mistake the first time. The
  // count is what makes `total` non-zero, so gating it on `total` deadlocks and the page
  // renders "No products found" forever. The doomed products request for the bad page
  // does still go out once; the backend answers it as an empty page rather than a 500,
  // and `productsByPage[key]` becomes `[]`, so `hasProducts` stops it repeating.
  //
  // Replace, not push, or Back returns to the bad page and bounces off the correction.
  // No dep array: `setUrlState` derives from `useSearchParams`, whose setter changes
  // identity on every URL change, so listing it would re-run this on every page change.
  const pageOutOfRange = total > 0 && page > totalPages;
  // One correction for every bad value, upper and lower: `safePage` is what the listing
  // will actually show, so writing it back covers `-1` → 1, `1.5` → 1 and `234234` → 925
  // in a single condition. `view` gets the same treatment for `?view=banana`.
  const safePage = pageOutOfRange ? totalPages : page;
  useEffect(() => {
    const patch = {};
    if (safePage !== rawPage) patch.page = safePage;
    if (view !== rawView) patch.view = view;
    if (Object.keys(patch).length > 0) setUrlState(patch, { replace: true });
  });

  // One flag for "the rows aren't ready yet". The favourites route has a second
  // fetch in front of the product fetch, so both have to settle before the real
  // grid can replace the skeleton.
  const showSkeleton =
    isLoading || (by === "favoriteItems" && favoritesLoading);

  // Pagination stays clickable during a fetch, so the user can supersede a slow
  // page before it lands. Keyed on the products cache key, so a re-run that isn't
  // a new target neither aborts nor duplicates the request in flight.
  const requestProducts = useSupersedingRequest();

  // Cache check selectors
  const initialCountKey = `${effectiveId}_`;
  const hasInitialCount = useSelector(
    (state) => state.products.totalCounts?.[initialCountKey] !== undefined
  );
  const hasFilteredCount = useSelector(
    (state) => state.products.totalCounts?.[countKey] !== undefined
  );
  const hasProducts = useSelector(
    (state) => state.products.productsByPage[key] !== undefined
  );

  // Fetch initial count
  useEffect(() => {
    if (!by || hasInitialCount) {
      return;
    }

    // For favoriteItems with no IDs, skip count fetch
    if (
      by === "favoriteItems" &&
      (!processedSearchIds || processedSearchIds.length === 0)
    ) {
      return;
    }

    const fetchConfig = {
      type: by,
      id: processedSearchIds,
    };

    // Add auth token for user-specific data
    if (by === "orderHistory" || by === "favoriteItems") {
      fetchConfig.getAccessTokenSilently = getAccessTokenSilently;
    }

    dispatch(fetchCountBy(fetchConfig));
  }, [
    dispatch,
    by,
    processedSearchIds,
    hasInitialCount,
    getAccessTokenSilently,
  ]);

  // Fetch filtered count (currently no filters, but structure for future)
  useEffect(() => {
    if (!by || hasFilteredCount) {
      return;
    }

    // For favoriteItems with no IDs, skip count fetch
    if (
      by === "favoriteItems" &&
      (!processedSearchIds || processedSearchIds.length === 0)
    ) {
      return;
    }

    const fetchConfig = {
      type: by,
      id: processedSearchIds,
      minPrice: "",
      maxPrice: "",
      searchByName: searchByName,
      selectedCategories: "",
      selectedBrands: "",
    };

    // Add auth token for user-specific data
    if (by === "orderHistory" || by === "favoriteItems") {
      fetchConfig.getAccessTokenSilently = getAccessTokenSilently;
    }

    dispatch(fetchCountBy(fetchConfig));
  }, [
    dispatch,
    by,
    processedSearchIds,
    hasFilteredCount,
    getAccessTokenSilently,
    searchByName,
  ]);

  // Fetch products for current page
  useEffect(() => {
    // Return early if no 'by' prop provided or data already exists in cache
    if (!by || hasProducts) {
      return;
    }

    // Process search IDs inside the effect to avoid dependency issues
    const currentProcessedSearchIds =
      by === "favoriteItems"
        ? searchIds
          ? searchIds
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id !== "")
              .map((id) => Number(id))
              .filter((id) => !isNaN(id))
          : [] // Empty array when no searchIds for favorites
        : searchIds;

    // For favoriteItems with no IDs, don't make API call - just mark as complete
    if (
      by === "favoriteItems" &&
      (!currentProcessedSearchIds || currentProcessedSearchIds.length === 0)
    ) {
      return; // Exit early without API call
    }

    // Load products
    const fetchConfig = {
      type: by,
      id: currentProcessedSearchIds, // Pass the array directly for favoriteItems
      page: page,
      limit: perPage,
      sort: sort,
      minPrice: "",
      maxPrice: "",
      searchByName: searchByName,
      selectedCategories: "",
      selectedBrands: "",
    };

    // Add auth token for user-specific data
    if (by === "orderHistory" || by === "favoriteItems") {
      fetchConfig.getAccessTokenSilently = getAccessTokenSilently;
    }

    // A page the user has already navigated past is wasted NetSuite work, and
    // letting it settle would fight the newer request over the slice's single
    // isLoading flag — requestProducts aborts it, but only when the key changed.
    requestProducts(key, () => dispatch(fetchProductsBy(fetchConfig)));
  }, [
    dispatch,
    requestProducts,
    key,
    searchIds,
    by,
    getAccessTokenSilently,
    hasProducts,
    page,
    perPage,
    sort,
    searchByName,
  ]);

  // Width and horizontal padding come from the route's CenteredContent.
  // CenteredContent now supports opting out of its max-width, and this page
  // uses that option so it isn't capped at CenteredContent's default
  // max-w-screen-xl — the grid needs the extra room to show 5-up on large
  // screens.
  return (
    <div className="py-6 lg:py-8">
      {/* Error display */}
      {error && (
        <ErrorMessage
          message={`Failed to load ${by.replace(/([A-Z])/g, " $1").toLowerCase()}. Please try again later.`}
        />
      )}

      {/* No products message */}
      {!showSkeleton && !error && products.length === 0 && (
        <div className="text-gray-500 py-8 text-center">
          No products found in your{" "}
          {by.replace(/([A-Z])/g, " $1").toLowerCase()}.
        </div>
      )}

      {/* Toolbar with Grid/List toggle, sorting, and per-page options.
                Stays mounted while loading — BEHAVIOR.md swaps only the grid for
                the skeleton, so the chrome must not unmount and re-flow. */}
      {!error && (showSkeleton || products.length > 0) && (
        <div className="mb-4">
          <ProductToolbar
            page={page}
            shown={products.length}
            perPageOptions={[12, 24, 48]}
            onPerPageChange={(e) =>
              setUrlState({ perPage: Number(e.target.value), page: 1 })
            }
            perPage={perPage}
            sort={sort}
            onSortChange={(e) => setUrlState({ sort: e.target.value })}
            total={total}
            view={view}
            onViewChange={setView}
            showSort={false}
            showPerPage={true}
            loading={showSkeleton}
          />
        </div>
      )}

      {/* Top pagination. Gated on totalPages (stable across a page change)
                rather than the page-keyed products array, so it dims via
                `loading` instead of unmounting. */}
      {!error && totalPages > 1 && (
        <div className="mb-4">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            loading={showSkeleton}
          />
        </div>
      )}

      {/* Shimmer stand-in for the incoming page, matching the active view */}
      {showSkeleton && !error && (
        <ProductsSkeleton view={view} count={perPage} />
      )}

      {/* Products display with view toggle */}
      {!showSkeleton &&
        !error &&
        products.length > 0 &&
        (view === "grid" ? (
          <div className="pc-grid [animation:dndRevealUp_.38s_var(--ease-out)_both]">
            {products.map((product) => (
              <ListProduct key={product.id} product={product} listType="grid" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-[10px] [animation:dndRevealUp_.38s_var(--ease-out)_both]">
            <div className="contents">
              {products.map((product) => (
                <ListProduct
                  key={product.id}
                  product={product}
                  listType="list"
                />
              ))}
            </div>
          </div>
        ))}

      {/* Bottom pagination */}
      {!error && totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            loading={showSkeleton}
          />
        </div>
      )}
    </div>
  );
}
